// BottomNav — navegação mobile por tabs no rodapé (substitui a Sidebar em ≤768px).
// Mostra os itens `mobilePrimary` do papel + um botão "Mais" que abre o MoreSheet
// com o overflow. Some na rota da thread (/conversas/:id) — padrão de app de chat:
// o composer precisa do rodapé inteiro.
import { useState } from 'react';
import { NavLink, useLocation, useMatch } from 'react-router-dom';
import { useAppSelector } from '@/app/store/hooks';
import { ROUTES } from '@/app/router/paths';
import { selectRole } from '@/features/auth';
import { selectUnreadConversationCount } from '@/features/notifications';
import { MoreHorizontalIcon } from '@/components/icons';
import { useIsMobile } from '@/lib/hooks/useMediaQuery';
import { cn } from '@/lib/utils/cn';
import { mobileNavForRole } from './navItems';
import { MoreSheet } from './MoreSheet';
import styles from './BottomNav.module.css';

// Rota da thread (/conversas/:id) — mas NÃO a fila (/conversas/fila, que casa o
// mesmo padrão dinâmico). Só a thread real libera o rodapé pro composer.
function useOnThread(): boolean {
  const convMatch = useMatch(ROUTES.inboxConversation) !== null;
  const queueMatch = useMatch(ROUTES.inboxQueue) !== null;
  return convMatch && !queueMatch;
}

/**
 * A BottomNav está visível? Falsa na thread (/conversas/:id) e no desktop.
 * Contrato compartilhado com o AppShell, que usa isso pro padding do .main.
 */
export function useBottomNavVisible(): boolean {
  const onThread = useOnThread();
  const isMobile = useIsMobile();
  return isMobile && !onThread;
}

// A rota atual pertence a algum item de overflow (fora do rodapé)? → "Mais" ativo.
function pathBelongsTo(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function BottomNav() {
  const onThread = useOnThread();
  const role = useAppSelector(selectRole);
  const unreadCount = useAppSelector(selectUnreadConversationCount);
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Thread aberta → libera o rodapé pro composer (padrão WhatsApp).
  if (onThread) return null;

  const { primary, overflow } = role
    ? mobileNavForRole(role)
    : { primary: [], overflow: [] };

  const moreActive = overflow.some((it) => pathBelongsTo(location.pathname, it.to));

  return (
    <>
      <nav className={styles.nav} aria-label="Navegação principal">
        {primary.map((item) => {
          // Badge de mensagens novas só na "Conversas" (rota do inbox).
          const badge = item.to === ROUTES.inbox && unreadCount > 0 ? unreadCount : 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => cn(styles.tab, isActive && styles.active)}
            >
              <span className={styles.iconWrap}>
                <item.Icon size={22} />
                {badge > 0 && (
                  <span
                    className={styles.badge}
                    aria-label={`${badge} conversa${badge > 1 ? 's' : ''} com mensagens novas`}
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </span>
              <span className={styles.label}>{item.shortLabel ?? item.label}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          className={cn(styles.tab, moreActive && styles.active)}
          aria-expanded={moreOpen}
          aria-haspopup="dialog"
          onClick={() => setMoreOpen(true)}
        >
          <span className={styles.iconWrap}>
            <MoreHorizontalIcon size={22} />
          </span>
          <span className={styles.label}>Mais</span>
        </button>
      </nav>
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
